import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  ScrollView,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useAuth } from "../../contexts/AuthContext";
import {
  getAdminUsers,
  updateAdminUserType,
  getAdminActivity,
  revertAdminActivity,
  getDeletionRequests,
  approveDeletionRequest,
  rejectDeletionRequest,
} from "../../services/parseService";

type AdminUser = {
  objectId: string;
  username?: string;
  email?: string;
  userType?: "admin" | "super_admin";
  createdAt?: string;
};

type ActivityTargetType = "all" | "opportunity" | "issue" | "suggestion" | "featured" | "user";

const REVERTIBLE_ACTIONS = new Set([
  "opportunity.update",
  "opportunity.create",
  "opportunity.delete",
  "issue.updateStatus",
  "suggestion.updateStatus",
  "featured.set",
  "admin.updateUserType",
]);

const SuperAdminScreen: React.FC = () => {
  const { currentUser } = useAuth();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [deletionRequests, setDeletionRequests] = useState<any[]>([]);
  const [selectedAdminId, setSelectedAdminId] = useState<string | null>(null);
  const [targetTypeFilter, setTargetTypeFilter] = useState<ActivityTargetType>("all");
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(true);
  const [isLoadingActivity, setIsLoadingActivity] = useState(true);
  const [isLoadingDeletionRequests, setIsLoadingDeletionRequests] = useState(true);
  const [isUpdatingRole, setIsUpdatingRole] = useState<string | null>(null);
  const [isReverting, setIsReverting] = useState<string | null>(null);
  const [isReviewingRequest, setIsReviewingRequest] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentUserId = currentUser?.id;
  const mountedRef = useRef(true);
  const activityAbortRef = useRef<AbortController | null>(null);

  const loadAdmins = useCallback(async () => {
    if (!mountedRef.current) return;
    setIsLoadingAdmins(true);
    try {
      const results = await getAdminUsers();
      if (!mountedRef.current) return;
      const sorted = results.sort((a: AdminUser, b: AdminUser) => {
        if (a.userType === b.userType) return 0;
        return a.userType === "super_admin" ? -1 : 1;
      });
      setAdmins(sorted);
    } catch (err) {
      if (mountedRef.current) {
        setError("Failed to load admin users.");
      }
    } finally {
      if (mountedRef.current) {
        setIsLoadingAdmins(false);
      }
    }
  }, []);

  const loadActivity = useCallback(async () => {
    if (!mountedRef.current) return;
    
    // Cancel previous request to prevent race conditions
    if (activityAbortRef.current) {
      activityAbortRef.current.abort();
    }
    activityAbortRef.current = new AbortController();

    setIsLoadingActivity(true);
    try {
      const targetType = targetTypeFilter === "all" ? null : targetTypeFilter;
      const results = await getAdminActivity({
        actorId: selectedAdminId || null,
        targetType,
        limit: 100,
      });
      if (!mountedRef.current) return;
      setActivities(results);
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      if (mountedRef.current) {
        setError("Failed to load admin activity.");
      }
    } finally {
      if (mountedRef.current) {
        setIsLoadingActivity(false);
      }
    }
  }, [selectedAdminId, targetTypeFilter]);

  const loadDeletionRequests = useCallback(async () => {
    if (!mountedRef.current) return;
    setIsLoadingDeletionRequests(true);
    try {
      const results = await getDeletionRequests("Pending");
      if (!mountedRef.current) return;
      setDeletionRequests(results);
    } catch (err) {
      if (mountedRef.current) {
        setError("Failed to load deletion requests.");
      }
    } finally {
      if (mountedRef.current) {
        setIsLoadingDeletionRequests(false);
      }
    }
  }, []);

  useEffect(() => {
    loadAdmins();
    loadDeletionRequests();
  }, [loadAdmins, loadDeletionRequests]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (activityAbortRef.current) {
        activityAbortRef.current.abort();
      }
    };
  }, []);

  const filteredAdmins = useMemo(() => admins, [admins]);

  const formatAdminName = (admin: AdminUser) =>
    admin.username || admin.email || admin.objectId;

  const formatActivityTime = (timestamp: string) => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "Unknown";
    return date.toLocaleString();
  };

  const canRevert = (activity: any) => REVERTIBLE_ACTIONS.has(activity?.action);

  const confirmRoleChange = async (admin: AdminUser, newRole: "admin" | "super_admin") => {
    const name = formatAdminName(admin);
    const confirmed = window.confirm(
      `Confirm Role Change\n\nChange ${name} to ${newRole.replace("_", " ")}?`
    );
    if (!confirmed) return;

    setIsUpdatingRole(admin.objectId);
    const success = await updateAdminUserType(
      admin.objectId,
      newRole,
      admin.userType,
    );
    setIsUpdatingRole(null);
    if (success) {
      window.alert("Success: Role updated");
      loadAdmins();
      loadActivity();
    } else {
      window.alert("Error: Failed to update role");
    }
  };

  const confirmRevert = async (activity: any) => {
    console.log("confirmRevert called with activity:", activity);
    
    // Validation
    if (!activity || !activity.action) {
      window.alert("Error: Invalid activity record");
      return;
    }

    // Prevent reverting reverts to avoid circular loops
    if (activity.action === 'opportunity.restore') {
      window.alert("Cannot revert a restore operation. Please delete the restored opportunity instead.");
      return;
    }

    // Check if already being reverted
    if (isReverting === (activity.objectId || activity.id)) {
      return;
    }

    const revertMessage =
      activity?.action === "opportunity.delete"
        ? "This will restore the opportunity as a new record."
        : "This will undo the change.";

    const confirmed = window.confirm(`Revert Change\n\n${revertMessage}\n\nAre you sure?`);
    if (!confirmed) return;

    const activityId = activity.objectId || activity.id;
    try {
      setIsReverting(activityId);
      await revertAdminActivity(activity);
      if (!mountedRef.current) return;
      window.alert("Success: Change reverted successfully");
      loadActivity();
    } catch (err) {
      console.error("Revert error:", err);
      if (!mountedRef.current) return;
      const errorMessage = err instanceof Error ? err.message : String(err);
      window.alert(`Error: Failed to revert: ${errorMessage}`);
    } finally {
      if (mountedRef.current) {
        setIsReverting(null);
      }
    }
  };

  const handleApproveDeletion = async (request: any) => {
    console.log("handleApproveDeletion called with request:", request);
    
    // Validation
    if (!request || !request.objectId) {
      window.alert("Error: Invalid deletion request");
      return;
    }

    // Check if already processed
    if (request.status !== "Pending") {
      window.alert(`This request has already been ${request.status.toLowerCase()}`);
      return;
    }

    // Check if already processing
    const requestId = request.objectId || request.id;
    if (isReviewingRequest === requestId) {
      return;
    }

    const opportunityTitle = request.opportunity?.title || "Unknown";
    const confirmed = window.confirm(
      `Approve Deletion\n\nApprove deletion of "${opportunityTitle}"?\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setIsReviewingRequest(requestId);
      await approveDeletionRequest(request);
      if (!mountedRef.current) return;
      window.alert("Success: Deletion request approved and opportunity deleted");
      await loadDeletionRequests();
      await loadActivity();
    } catch (err) {
      console.error("Approve deletion error:", err);
      if (!mountedRef.current) return;
      const errorMessage = err instanceof Error ? err.message : String(err);
      window.alert(`Error: Failed to approve: ${errorMessage}`);
    } finally {
      if (mountedRef.current) {
        setIsReviewingRequest(null);
      }
    }
  };

  const handleRejectDeletion = async (request: any) => {
    console.log("handleRejectDeletion called with request:", request);
    
    // Validation
    if (!request || !request.objectId) {
      window.alert("Error: Invalid deletion request");
      return;
    }

    // Check if already processed
    if (request.status !== "Pending") {
      window.alert(`This request has already been ${request.status.toLowerCase()}`);
      return;
    }

    // Check if already processing
    const requestId = request.objectId || request.id;
    if (isReviewingRequest === requestId) {
      return;
    }

    const opportunityTitle = request.opportunity?.title || "Unknown";
    const confirmed = window.confirm(
      `Reject Deletion\n\nReject deletion request for "${opportunityTitle}"?`
    );
    if (!confirmed) return;

    try {
      setIsReviewingRequest(requestId);
      await rejectDeletionRequest(request);
      if (!mountedRef.current) return;
      window.alert("Success: Deletion request rejected");
      await loadDeletionRequests();
      await loadActivity();
    } catch (err) {
      console.error("Reject deletion error:", err);
      if (!mountedRef.current) return;
      const errorMessage = err instanceof Error ? err.message : String(err);
      window.alert(`Error: Failed to reject: ${errorMessage}`);
    } finally {
      if (mountedRef.current) {
        setIsReviewingRequest(null);
      }
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {error && (
        <View style={styles.errorBanner}>
          <Ionicons name="warning-outline" size={18} color="#DC2626" />
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Admin Team</Text>
          <TouchableOpacity style={styles.refreshButton} onPress={loadAdmins}>
            <Ionicons name="refresh" size={16} color="#6B7280" />
          </TouchableOpacity>
        </View>

        {isLoadingAdmins ? (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" color="#2563EB" />
            <Text style={styles.loadingText}>Loading admins...</Text>
          </View>
        ) : (
          filteredAdmins.map((admin) => {
            const isSelf = admin.objectId === currentUserId;
            return (
              <View key={admin.objectId} style={styles.adminCard}>
                <View style={styles.adminInfo}>
                  <Text style={styles.adminName}>{formatAdminName(admin)}</Text>
                  <Text style={styles.adminRole}>
                    {admin.userType === "super_admin" ? "Super Admin" : "Admin"}
                  </Text>
                </View>
                <View style={styles.adminActions}>
                  {admin.userType === "admin" && (
                    <TouchableOpacity
                      style={styles.roleButton}
                      onPress={() => confirmRoleChange(admin, "super_admin")}
                      disabled={isUpdatingRole === admin.objectId}
                    >
                      <Text style={styles.roleButtonText}>Make Super</Text>
                    </TouchableOpacity>
                  )}
                  {admin.userType === "super_admin" && !isSelf && (
                    <TouchableOpacity
                      style={[styles.roleButton, styles.roleButtonMuted]}
                      onPress={() => confirmRoleChange(admin, "admin")}
                      disabled={isUpdatingRole === admin.objectId}
                    >
                      <Text style={styles.roleButtonTextMuted}>Make Admin</Text>
                    </TouchableOpacity>
                  )}
                  {isSelf && (
                    <View style={styles.selfBadge}>
                      <Text style={styles.selfBadgeText}>You</Text>
                    </View>
                  )}
                </View>
              </View>
            );
          })
        )}
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Deletion Requests</Text>
          <TouchableOpacity style={styles.refreshButton} onPress={loadDeletionRequests}>
            <Ionicons name="refresh" size={16} color="#6B7280" />
          </TouchableOpacity>
        </View>

        {isLoadingDeletionRequests ? (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" color="#2563EB" />
            <Text style={styles.loadingText}>Loading requests...</Text>
          </View>
        ) : deletionRequests.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="trash-outline" size={28} color="#9CA3AF" />
            <Text style={styles.emptyText}>No pending deletion requests</Text>
          </View>
        ) : (
          deletionRequests.map((request) => {
            const requestId = request.objectId || request.id;
            const opportunityTitle =
              request.opportunity?.title ||
              request.opportunity?.attributes?.title ||
              request.opportunity?.objectId ||
              "Opportunity";
            const requestedBy =
              request.requestedByUsername ||
              request.requestedBy?.username ||
              request.requestedBy?.email ||
              "Unknown";

            return (
              <View key={requestId} style={styles.requestCard}>
                <Text style={styles.requestTitle}>{opportunityTitle}</Text>
                <Text style={styles.requestMeta}>Requested by {requestedBy}</Text>
                {request.reason ? (
                  <Text style={styles.requestReason}>Reason: {request.reason}</Text>
                ) : (
                  <Text style={styles.requestReason}>Reason: Not provided</Text>
                )}
                <View style={styles.requestActions}>
                  <TouchableOpacity
                    style={styles.requestApprove}
                    onPress={() => handleApproveDeletion(request)}
                    disabled={isReviewingRequest === requestId}
                  >
                    {isReviewingRequest === requestId ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Text style={styles.requestActionText}>Approve</Text>
                    )}
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.requestReject}
                    onPress={() => handleRejectDeletion(request)}
                    disabled={isReviewingRequest === requestId}
                  >
                    <Text style={styles.requestActionText}>Reject</Text>
                  </TouchableOpacity>
                </View>
              </View>
            );
          })
        )}
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Activity Feed</Text>
          <TouchableOpacity style={styles.refreshButton} onPress={loadActivity}>
            <Ionicons name="refresh" size={16} color="#6B7280" />
          </TouchableOpacity>
        </View>

        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View style={styles.filterRow}>
            {[
              { key: "all", label: "All" },
              { key: "opportunity", label: "Opportunities" },
              { key: "issue", label: "Issues" },
              { key: "suggestion", label: "Suggestions" },
              { key: "featured", label: "Featured" },
              { key: "user", label: "Admins" },
            ].map((filter) => (
              <TouchableOpacity
                key={filter.key}
                style={[
                  styles.filterChip,
                  targetTypeFilter === filter.key && styles.filterChipActive,
                ]}
                onPress={() => setTargetTypeFilter(filter.key as ActivityTargetType)}
              >
                <Text
                  style={[
                    styles.filterChipText,
                    targetTypeFilter === filter.key && styles.filterChipTextActive,
                  ]}
                >
                  {filter.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>

        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View style={styles.filterRow}>
            <TouchableOpacity
              style={[
                styles.filterChip,
                !selectedAdminId && styles.filterChipActive,
              ]}
              onPress={() => setSelectedAdminId(null)}
            >
              <Text
                style={[
                  styles.filterChipText,
                  !selectedAdminId && styles.filterChipTextActive,
                ]}
              >
                All Admins
              </Text>
            </TouchableOpacity>
            {admins.map((admin) => (
              <TouchableOpacity
                key={admin.objectId}
                style={[
                  styles.filterChip,
                  selectedAdminId === admin.objectId && styles.filterChipActive,
                ]}
                onPress={() => setSelectedAdminId(admin.objectId)}
              >
                <Text
                  style={[
                    styles.filterChipText,
                    selectedAdminId === admin.objectId && styles.filterChipTextActive,
                  ]}
                >
                  {formatAdminName(admin)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>

        {isLoadingActivity ? (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" color="#2563EB" />
            <Text style={styles.loadingText}>Loading activity...</Text>
          </View>
        ) : activities.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="time-outline" size={28} color="#9CA3AF" />
            <Text style={styles.emptyText}>No recent activity</Text>
          </View>
        ) : (
          activities.map((activity) => {
            const activityId = activity.objectId || activity.id;
            const actorName =
              activity.actorUsername ||
              activity.actor?.username ||
              activity.actor?.email ||
              "Unknown";

            return (
              <View key={activityId} style={styles.activityCard}>
                <View style={styles.activityHeader}>
                  <Text style={styles.activitySummary}>{activity.summary}</Text>
                  <Text style={styles.activityTime}>
                    {formatActivityTime(activity.createdAt)}
                  </Text>
                </View>
                <Text style={styles.activityMeta}>
                  {actorName} • {activity.action}
                </Text>
                {!!activity.targetId && (
                  <Text style={styles.activityMeta}>Target: {activity.targetId}</Text>
                )}
                {canRevert(activity) && (
                  <TouchableOpacity
                    style={styles.revertButton}
                    onPress={() => confirmRevert(activity)}
                    disabled={isReverting === activityId}
                  >
                    {isReverting === activityId ? (
                      <ActivityIndicator size="small" color="#DC2626" />
                    ) : (
                      <>
                        <Ionicons name="arrow-undo-outline" size={14} color="#DC2626" />
                        <Text style={styles.revertButtonText}>Revert</Text>
                      </>
                    )}
                  </TouchableOpacity>
                )}
              </View>
            );
          })
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },
  content: {
    padding: 20,
    paddingBottom: 40,
  },
  section: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 16,
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#111827",
    fontFamily: "Inter-SemiBold",
  },
  refreshButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  adminCard: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  adminInfo: {
    flex: 1,
  },
  adminName: {
    fontSize: 14,
    fontWeight: "600",
    color: "#111827",
    fontFamily: "Inter-SemiBold",
  },
  adminRole: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 2,
    fontFamily: "Inter-Regular",
  },
  adminActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  roleButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: "#EEF2FF",
    borderWidth: 1,
    borderColor: "#C7D2FE",
  },
  roleButtonText: {
    fontSize: 12,
    color: "#2563EB",
    fontFamily: "Inter-SemiBold",
  },
  roleButtonMuted: {
    backgroundColor: "#F9FAFB",
    borderColor: "#E5E7EB",
  },
  roleButtonTextMuted: {
    fontSize: 12,
    color: "#6B7280",
    fontFamily: "Inter-SemiBold",
  },
  selfBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: "#DBEAFE",
  },
  selfBadgeText: {
    fontSize: 11,
    color: "#2563EB",
    fontFamily: "Inter-SemiBold",
  },
  filterRow: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 12,
  },
  filterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
  },
  filterChipActive: {
    backgroundColor: "#EEF2FF",
    borderColor: "#2563EB",
  },
  filterChipText: {
    fontSize: 12,
    color: "#6B7280",
    fontFamily: "Inter-Medium",
  },
  filterChipTextActive: {
    color: "#2563EB",
    fontFamily: "Inter-SemiBold",
  },
  activityCard: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
    backgroundColor: "#FFFFFF",
  },
  activityHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },
  activitySummary: {
    fontSize: 14,
    fontWeight: "600",
    color: "#111827",
    flex: 1,
    fontFamily: "Inter-SemiBold",
  },
  activityTime: {
    fontSize: 11,
    color: "#9CA3AF",
    fontFamily: "Inter-Regular",
  },
  activityMeta: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 4,
    fontFamily: "Inter-Regular",
  },
  revertButton: {
    marginTop: 8,
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: "#FEF2F2",
    borderWidth: 1,
    borderColor: "#FECACA",
  },
  revertButtonText: {
    fontSize: 12,
    color: "#DC2626",
    fontFamily: "Inter-SemiBold",
  },
  loadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 12,
  },
  loadingText: {
    fontSize: 14,
    color: "#6B7280",
    fontFamily: "Inter-Regular",
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: 16,
  },
  emptyText: {
    marginTop: 8,
    color: "#9CA3AF",
    fontSize: 13,
    fontFamily: "Inter-Regular",
  },
  requestCard: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
    backgroundColor: "#FFFFFF",
  },
  requestTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#111827",
    fontFamily: "Inter-SemiBold",
  },
  requestMeta: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 4,
    fontFamily: "Inter-Regular",
  },
  requestReason: {
    fontSize: 12,
    color: "#374151",
    marginTop: 6,
    fontFamily: "Inter-Regular",
  },
  requestActions: {
    flexDirection: "row",
    gap: 8,
    marginTop: 10,
  },
  requestApprove: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: "#16A34A",
  },
  requestReject: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: "#DC2626",
  },
  requestActionText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontFamily: "Inter-SemiBold",
  },
  errorBanner: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    backgroundColor: "#FEF2F2",
    borderWidth: 1,
    borderColor: "#FECACA",
    borderRadius: 8,
    marginBottom: 12,
  },
  errorText: {
    marginLeft: 8,
    color: "#DC2626",
    fontSize: 13,
    fontFamily: "Inter-Medium",
  },
});

export default SuperAdminScreen;
